import "./App.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider } from "./context/AppContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AdminProvider } from "./context/AdminContext";
import Navbar from "./components/Navbar";
import MobileCartBar from "./components/MobileCartBar";
import CartSheet from "./components/CartSheet";
import BrandLogo from "./components/BrandLogo";
import LandingPage from "./pages/LandingPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import TelangRosellaLandingPage from "./pages/TelangRosellaLandingPage";
import SingleProductLandingPage from "./pages/SingleProductLandingPage";
import CheckoutShippingPage from "./pages/CheckoutShippingPage";
import CheckoutPaymentPage from "./pages/CheckoutPaymentPage";
import PaymentPage from "./pages/PaymentPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminStaffDashboardPage from "./pages/AdminStaffDashboardPage";
import AdminProductsPage from "./pages/AdminProductsPage";
import AdminCategoriesPage from "./pages/AdminCategoriesPage";
import AdminCouriersPage from "./pages/AdminCouriersPage";
import AdminPaymentMethodsPage from "./pages/AdminPaymentMethodsPage";
import AdminSalesPage from "./pages/AdminSalesPage";
import AdminCouponsPage from "./pages/AdminCouponsPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminLandingPagesPage from "./pages/AdminLandingPagesPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminProfilePage from "./pages/AdminProfilePage";
import CheckoutHistoryPage from "./pages/CheckoutHistoryPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-2 text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/admin/login" replace />;
};

// Role-based Route Component
const RoleRoute = ({ children, roles = [] }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-2 text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  if (roles.length > 0 && !roles.includes(user?.role)) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  return children;
};

// Smart Dashboard: routes to the right dashboard based on role
// Owner & superadmin can toggle between full analytics and operational dashboard
const SmartDashboard = () => {
  const { user } = useAuth();
  const [view, setView] = useState("analytics");

  if (user?.role === "superadmin" || user?.role === "owner") {
    return view === "analytics" ? <AdminDashboardPage staffToggle={setView} /> : <AdminStaffDashboardPage analyticsToggle={setView} />;
  }
  return <AdminStaffDashboardPage />;
};

// Public Layout Component
const PublicLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      {children}
      <MobileCartBar />
      <CartSheet />
      <footer className="mt-20 border-t border-stone-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-10 text-center sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <BrandLogo className="h-14 w-14" padded rounded="rounded-2xl" />
          </div>
          <p className="mt-3 font-display text-xl font-semibold text-emerald-700">
            Teras Alami
          </p>
          <p className="mt-2 text-stone-500">
            Herbal alami untuk kesehatan keluarga Indonesia.
          </p>
          <p
            className="mx-auto mt-5 max-w-xl text-xs text-stone-500"
            data-testid="footer-disclaimer"
          >
            Produk ini merupakan minuman herbal alami dan bukan pengganti
            pengobatan medis.
          </p>
          <nav
            aria-label="Footer"
            className="mt-5 flex flex-wrap items-center justify-center gap-3 text-sm text-stone-500"
          >
            <a className="hover:text-emerald-700" href="/">
              Beranda
            </a>
            <a className="hover:text-emerald-700" href="/telang-rosella">
              Teh Rosella & Bunga Telang
            </a>
            <a className="hover:text-emerald-700" href="/tentang">
              Tentang Kami
            </a>
            <a className="hover:text-emerald-700" href="/kontak">
              Kontak
            </a>
            <a className="hover:text-emerald-700" href="/riwayat">
              Riwayat Checkout
            </a>
          </nav>
          <p className="mt-4 text-sm text-stone-400">
            © {new Date().getFullYear()} Teras Alami. Semua hak dilindungi.
          </p>
        </div>
      </footer>
    </>
  );
};

function App() {
  return (
    <div className="App min-h-screen bg-stone-50 text-stone-900">
      <AppProvider>
        <AuthProvider>
          <AdminProvider>
            <BrowserRouter>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={
                  <PublicLayout>
                    <LandingPage />
                  </PublicLayout>
                } />
                <Route path="/telang-rosella" element={
                  <PublicLayout>
                    <TelangRosellaLandingPage />
                  </PublicLayout>
                } />
                <Route path="/lp/:slug" element={
                  <PublicLayout>
                    <SingleProductLandingPage />
                  </PublicLayout>
                } />
                <Route path="/tentang" element={
                  <PublicLayout>
                    <AboutPage />
                  </PublicLayout>
                } />
                <Route path="/kontak" element={
                  <PublicLayout>
                    <ContactPage />
                  </PublicLayout>
                } />
                <Route path="/produk/:slug" element={
                  <PublicLayout>
                    <ProductDetailPage />
                  </PublicLayout>
                } />
                <Route path="/checkout/pengiriman" element={
                  <PublicLayout>
                    <CheckoutShippingPage />
                  </PublicLayout>
                } />
                <Route path="/checkout/pembayaran" element={
                  <PublicLayout>
                    <CheckoutPaymentPage />
                  </PublicLayout>
                } />
                <Route path="/payment/:invoiceId" element={
                  <PublicLayout>
                    <PaymentPage />
                  </PublicLayout>
                } />
                <Route path="/pembayaran/:invoiceId" element={
                  <PublicLayout>
                    <PaymentPage />
                  </PublicLayout>
                } />
                <Route path="/sukses/:invoiceId" element={
                  <PublicLayout>
                    <PaymentSuccessPage />
                  </PublicLayout>
                } />
                <Route path="/riwayat" element={
                  <PublicLayout>
                    <CheckoutHistoryPage />
                  </PublicLayout>
                } />
                
                {/* Admin Login (Public) */}
                <Route path="/admin/login" element={<AdminLoginPage />} />
                
                {/* Protected Admin Routes */}
                <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/admin/dashboard" element={
                  <ProtectedRoute>
                    <SmartDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/admin/produk" element={
                  <RoleRoute roles={["superadmin", "owner", "admin"]}>
                    <AdminProductsPage />
                  </RoleRoute>
                } />
                <Route path="/admin/kategori" element={
                  <RoleRoute roles={["superadmin", "owner"]}>
                    <AdminCategoriesPage />
                  </RoleRoute>
                } />
                <Route path="/admin/kurir" element={
                  <RoleRoute roles={["superadmin", "owner", "admin"]}>
                    <AdminCouriersPage />
                  </RoleRoute>
                } />
                <Route path="/admin/pembayaran" element={
                  <RoleRoute roles={["superadmin", "owner"]}>
                    <AdminPaymentMethodsPage />
                  </RoleRoute>
                } />
                <Route path="/admin/penjualan" element={
                  <RoleRoute roles={["superadmin", "owner", "admin"]}>
                    <AdminSalesPage />
                  </RoleRoute>
                } />
                <Route path="/admin/kupon" element={
                  <RoleRoute roles={["superadmin", "owner", "admin"]}>
                    <AdminCouponsPage />
                  </RoleRoute>
                } />
                <Route path="/admin/landing-pages" element={
                  <RoleRoute roles={["superadmin", "owner"]}>
                    <AdminLandingPagesPage />
                  </RoleRoute>
                } />
                <Route path="/admin/users" element={
                  <RoleRoute roles={["superadmin"]}>
                    <AdminUsersPage />
                  </RoleRoute>
                } />
                <Route path="/admin/profil" element={
                  <ProtectedRoute>
                    <AdminProfilePage />
                  </ProtectedRoute>
                } />
              </Routes>

              <Toaster position="top-center" richColors />
            </BrowserRouter>
          </AdminProvider>
        </AuthProvider>
      </AppProvider>
    </div>
  );
}

export default App;
